import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState, useCallback } from 'react';
import api, { BASE_URL } from '../api';
import { useAuth } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

// ─── Sabitler ─────────────────────────────────────────────────────────────────
const BASLANGIC = 8;   // 08:00
const BITIS = 23;      // 23:00
const SAAT_PX = 80;    // px per hour
const TOPLAM_PX = (BITIS - BASLANGIC) * SAAT_PX;
const SAATLER = Array.from({ length: BITIS - BASLANGIC + 1 }, (_, i) => BASLANGIC + i);

const TIP_RENK: Record<string, { bg: string; border: string; text: string; etiket: string }> = {
  ders:      { bg: '#fff0f0', border: '#C41E3A', text: '#9B1430', etiket: 'Ders' },
  kiralama:  { bg: '#f0fdf4', border: '#16a34a', text: '#15803d', etiket: 'Kiralama' },
  etkinlik:  { bg: '#eff6ff', border: '#2563eb', text: '#1e40af', etiket: 'Etkinlik' },
  ozel_ders: { bg: '#fff7ed', border: '#ea580c', text: '#c2410c', etiket: 'Özel Ders' },
  prova:     { bg: '#ecfeff', border: '#0891b2', text: '#0e7490', etiket: 'Prova' },
  calisma:   { bg: '#f5f3ff', border: '#7c3aed', text: '#6d28d9', etiket: 'Çalışma' },
  diger:     { bg: '#f9fafb', border: '#9ca3af', text: '#6b7280', etiket: 'Diğer' },
};

const TR_GUN = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const TR_AY  = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];

// ─── Yardımcı fonksiyonlar ────────────────────────────────────────────────────
function bugun(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function formatTarih(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00Z');
  return `${TR_GUN[d.getUTCDay()]}, ${d.getUTCDate()} ${TR_AY[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
const s5 = (t: any) => String(t || '').slice(0, 5);
function saatPx(s: any): number {
  const [h, m] = String(s).split(':').map(Number);
  return ((h - BASLANGIC) * 60 + (m || 0)) * SAAT_PX / 60;
}
function yukPx(bas: any, bit: any): number {
  const [h1, m1] = String(bas).split(':').map(Number);
  const [h2, m2] = String(bit).split(':').map(Number);
  return Math.max(((h2 - h1) * 60 + (m2 - m1)) * SAAT_PX / 60, 28);
}

// ─── Tipler ──────────────────────────────────────────────────────────────────
interface Salon { id: string; ad: string; }
interface TakvimVeri {
  tarih: string; gun: string;
  salonlar: Salon[];
  dersler: any[];
  etkinlikler: any[];
  ozel_dersler: any[];
}
interface Kolon { id: string | null; ad: string; }
interface CalEvent {
  id: string; baslik: string; baslangic_saat: string; bitis_saat: string;
  tip: string; alt: string; duzenleneblir: boolean; kaynak: any;
}

const BOŞ_FORM = {
  baslik: '', tip: 'ozel_ders', tarih: '', baslangic_saat: '10:00', bitis_saat: '11:00',
  salon_id: '', egitmen_id: '', ogrenci_ad: '', notlar: '',
};

// ─── Kolon eventlerini derle ──────────────────────────────────────────────────
function kolonEvents(veri: TakvimVeri, salonId: string | null): CalEvent[] {
  const list: CalEvent[] = [];

  veri.dersler
    .filter(d => salonId ? d.salon_id === salonId : !d.salon_id)
    .forEach(d => list.push({
      id: d.id, baslik: d.ad, baslangic_saat: d.baslangic_saat, bitis_saat: d.bitis_saat,
      tip: 'ders',
      alt: (d.egitmenler || []).map((e: any) => `${e.ad} ${e.soyad}`).join(', '),
      duzenleneblir: false, kaynak: d,
    }));

  veri.etkinlikler.forEach(e => {
    const inCol = salonId
      ? (e.salonlar || []).some((s: any) => s.id === salonId)
      : !(e.salonlar || []).length;
    if (!inCol) return;
    list.push({
      id: e.id, baslik: e.ad, baslangic_saat: e.baslangic_saat, bitis_saat: e.bitis_saat,
      tip: e.tip === 'kiralama' ? 'kiralama' : 'etkinlik',
      alt: e.kiralayan || e.aciklama || '',
      duzenleneblir: false, kaynak: e,
    });
  });

  veri.ozel_dersler
    .filter(o => salonId ? o.salon_id === salonId : !o.salon_id)
    .forEach(o => list.push({
      id: o.id, baslik: o.baslik, baslangic_saat: o.baslangic_saat, bitis_saat: o.bitis_saat,
      tip: o.tip || 'ozel_ders',
      alt: [o.egitmen_ad ? `${o.egitmen_ad} ${o.egitmen_soyad || ''}`.trim() : '', o.ogrenci_ad]
        .filter(Boolean).join(' → '),
      duzenleneblir: true, kaynak: o,
    }));

  return list.sort((a, b) => String(a.baslangic_saat).localeCompare(String(b.baslangic_saat)));
}

// ─── Ana bileşen ─────────────────────────────────────────────────────────────
export default function Takvim() {
  usePageTitle('Takvim');
  const { kullanici } = useAuth() as any;
  const isAdmin = ['admin', 'super_admin', 'sube_admin'].includes(kullanici?.rol);
  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';

  const [tarih, setTarih] = useState(bugun());
  const [veri, setVeri] = useState<TakvimVeri | null>(null);
  const [egitmenler, setEgitmenler] = useState<any[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [modal, setModal] = useState<{ open: boolean; id: string | null; form: typeof BOŞ_FORM }>({
    open: false, id: null, form: { ...BOŞ_FORM },
  });
  const [kaydediyor, setKaydediyor] = useState(false);
  const [icalUrl, setIcalUrl] = useState<string | null>(null);

  const yukle = useCallback(async (t: string) => {
    setYukleniyor(true);
    try {
      const reqs: Promise<any>[] = [
        api.get(`/api/takvim?tarih=${t}`),
        isAdmin ? api.get('/api/egitmenler') : Promise.resolve({ data: [] }),
      ];
      if (isAdmin && !icalUrl) reqs.push(api.get('/api/takvim/ical-token/sube'));
      const results = await Promise.allSettled(reqs);
      if (results[0].status === 'fulfilled') setVeri(results[0].value.data);
      if (results[1].status === 'fulfilled') setEgitmenler(results[1].value.data || []);
      if (results[2]?.status === 'fulfilled') {
        const { token, sube_id } = results[2].value.data;
        setIcalUrl(`${BASE_URL}/api/takvim/ical/sube/${sube_id}?token=${token}`);
      }
    } finally { setYukleniyor(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => { yukle(tarih); }, [tarih, yukle]);

  // ── Date navigation ────────────────────────────────────────────────────────
  const gitTarih = (n: number) => setTarih(t => addDays(t, n));

  // ── Modal helpers ──────────────────────────────────────────────────────────
  function yeniAc(salon_id = '', baslangic_saat = '10:00') {
    const [h, m] = baslangic_saat.split(':').map(Number);
    const bitH = h + 1 >= BITIS ? BITIS - 1 : h + 1;
    const bitis_saat = `${String(bitH).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
    setModal({ open: true, id: null, form: { ...BOŞ_FORM, tarih, salon_id, baslangic_saat, bitis_saat } });
  }

  function duzenleAc(o: any) {
    setModal({
      open: true, id: o.id,
      form: {
        baslik: o.baslik || '', tip: o.tip || 'ozel_ders', tarih: o.tarih || tarih,
        baslangic_saat: s5(o.baslangic_saat), bitis_saat: s5(o.bitis_saat),
        salon_id: o.salon_id || '', egitmen_id: o.egitmen_id || '',
        ogrenci_ad: o.ogrenci_ad || '', notlar: o.notlar || '',
      },
    });
  }

  function kapat() { setModal(m => ({ ...m, open: false })); }

  const setF = (k: string, v: string) =>
    setModal(m => ({ ...m, form: { ...m.form, [k]: v } }));

  async function kaydet() {
    if (!modal.form.baslik || !modal.form.tarih || !modal.form.baslangic_saat || !modal.form.bitis_saat)
      return alert('Başlık, tarih ve saatler zorunlu');
    setKaydediyor(true);
    try {
      if (modal.id) {
        await api.put(`/api/takvim/ozel/${modal.id}`, modal.form);
      } else {
        await api.post('/api/takvim/ozel', modal.form);
      }
      kapat();
      yukle(tarih);
    } catch (e: any) {
      alert(e.response?.data?.hata || 'Kaydedilemedi');
    } finally { setKaydediyor(false); }
  }

  async function sil() {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Bu özel ders silinsin mi?')) return;
    await api.delete(`/api/takvim/ozel/${modal.id}`);
    kapat();
    yukle(tarih);
  }

  // ── Kolonlar ───────────────────────────────────────────────────────────────
  const kolonlar: Kolon[] = veri ? [
    ...veri.salonlar.map(s => ({ id: s.id, ad: s.ad })),
    ...(
      veri.dersler.some(d => !d.salon_id) ||
      veri.etkinlikler.some(e => !(e.salonlar || []).length) ||
      veri.ozel_dersler.some(o => !o.salon_id)
        ? [{ id: null, ad: 'Genel / Dış' }] : []
    ),
  ] : [];

  const renk = (t: string) => TIP_RENK[t] || TIP_RENK['diger'];

  const navBtnDynamic: React.CSSProperties = {
    border: `1px solid ${isDark ? '#333' : '#ddd'}`, background: card, borderRadius: 6,
    padding: '5px 12px', cursor: 'pointer', fontSize: 16, color: isDark ? '#bbb' : '#555',
  };

  const inpDynamic: React.CSSProperties = {
    width: '100%', padding: '8px 10px', border: `1px solid ${isDark ? '#333' : '#ddd'}`,
    borderRadius: 7, fontSize: 13, boxSizing: 'border-box', outline: 'none',
    background: card, color: isDark ? '#e8e8e8' : undefined,
  };

  const lblDynamic: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 600, color: isDark ? '#bbb' : '#555', marginBottom: 4, marginTop: 12,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: cardAlt, fontFamily: 'sans-serif' }}>

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div style={{ background: card, borderBottom: `1px solid ${border}`, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>Stüdyo Takvimi</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 16 }}>
          <button onClick={() => gitTarih(-1)} style={navBtnDynamic}>‹</button>
          <input
            type="date" value={tarih} onChange={e => setTarih(e.target.value)}
            style={{ border: `1px solid ${isDark ? '#333' : '#ddd'}`, borderRadius: 6, padding: '5px 10px', fontSize: 13, cursor: 'pointer', background: card, color: isDark ? '#e8e8e8' : undefined }}
          />
          <button onClick={() => gitTarih(1)} style={navBtnDynamic}>›</button>
          <button onClick={() => setTarih(bugun())} style={{ ...navBtnDynamic, fontSize: 11, padding: '5px 10px' }}>Bugün</button>
        </div>

        <div style={{ fontSize: 14, fontWeight: 600, color: isDark ? '#ccc' : '#444', flex: 1 }}>
          {veri ? formatTarih(veri.tarih) : ''}
        </div>

        {/* Lejant */}
        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          {Object.entries(TIP_RENK).slice(0, 6).map(([k, v]) => (
            <span key={k} style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: v.border, display: 'inline-block' }} />
              {v.etiket}
            </span>
          ))}
        </div>

        {icalUrl && (
          <button
            onClick={() => { navigator.clipboard.writeText(icalUrl); alert('iCal URL kopyalandı! Google Calendar → Diğer takvimler → URL\'den ekle'); }}
            style={{ ...ekleBtn, background: '#16a34a' }}
            title={icalUrl}
          >
            📅 iCal
          </button>
        )}
        <button onClick={() => yeniAc()} style={ekleBtn}>+ Özel Ders</button>
      </div>

      {/* ── Takvim Gövdesi ──────────────────────────────────────────────── */}
      {yukleniyor ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: textMuted }}>
          Yükleniyor…
        </div>
      ) : !veri ? null : (
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

          {/* Salon başlıkları + grid: tek scrollable alan */}
          <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>

            {/* Salon başlık satırı (sticky top) */}
            <div style={{ display: 'flex', position: 'sticky', top: 0, zIndex: 20, background: card, borderBottom: `2px solid ${border}`, flexShrink: 0 }}>
              <div style={{ width: 60, flexShrink: 0 }} />
              {kolonlar.length === 0 && (
                <div style={{ flex: 1, padding: '12px', color: textMuted, fontSize: 13, textAlign: 'center' }}>
                  Salon bulunamadı
                </div>
              )}
              {kolonlar.map(k => (
                <div key={k.id || 'genel'} style={{
                  flex: 1, minWidth: 160, padding: '10px 12px', borderLeft: `1px solid ${border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: isDark ? '#e0e0e0' : '#333' }}>{k.ad}</span>
                  <button
                    onClick={() => yeniAc(k.id || '', '10:00')}
                    title="Özel ders ekle"
                    style={{ background: 'none', border: `1px solid ${isDark ? '#333' : '#ddd'}`, borderRadius: 5, width: 24, height: 24, cursor: 'pointer', fontSize: 14, color: textSec, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                  >+</button>
                </div>
              ))}
            </div>

            {/* Saat grid + kolonlar */}
            <div style={{ display: 'flex', flex: 1 }}>

              {/* Saat etiketi sütunu (sticky left) */}
              <div style={{ width: 60, flexShrink: 0, position: 'sticky', left: 0, zIndex: 10, background: cardAlt, borderRight: `1px solid ${border}` }}>
                <div style={{ position: 'relative', height: TOPLAM_PX }}>
                  {SAATLER.map(h => (
                    <div key={h} style={{
                      position: 'absolute', top: (h - BASLANGIC) * SAAT_PX - 9,
                      right: 8, fontSize: 11, color: textMuted, userSelect: 'none',
                    }}>
                      {String(h).padStart(2,'0')}:00
                    </div>
                  ))}
                </div>
              </div>

              {/* Salon kolonları */}
              {kolonlar.map(k => {
                const events = kolonEvents(veri, k.id);
                return (
                  <div key={k.id || 'genel'} style={{ flex: 1, minWidth: 160, borderLeft: `1px solid ${border}`, position: 'relative' }}>
                    <div style={{ position: 'relative', height: TOPLAM_PX }}>

                      {/* Saat grid çizgileri */}
                      {SAATLER.map(h => (
                        <React.Fragment key={h}>
                          <div style={{ position: 'absolute', top: (h - BASLANGIC) * SAAT_PX, left: 0, right: 0, borderTop: `1px solid ${borderSub}` }} />
                          {h < BITIS && (
                            <div style={{ position: 'absolute', top: (h - BASLANGIC) * SAAT_PX + SAAT_PX / 2, left: 0, right: 0, borderTop: `1px dashed ${isDark ? '#1c1c1c' : '#f5f5f5'}` }} />
                          )}
                        </React.Fragment>
                      ))}

                      {/* Etkinlik blokları */}
                      {events.map(ev => {
                        const r = renk(ev.tip);
                        const topY = saatPx(ev.baslangic_saat);
                        const h = yukPx(ev.baslangic_saat, ev.bitis_saat);
                        return (
                          <div
                            key={ev.id}
                            onClick={() => ev.duzenleneblir && duzenleAc(ev.kaynak)}
                            style={{
                              position: 'absolute',
                              top: topY + 1, left: 4, right: 4,
                              height: h - 2,
                              background: r.bg,
                              border: `1.5px solid ${r.border}`,
                              borderRadius: 6,
                              padding: '3px 7px',
                              overflow: 'hidden',
                              cursor: ev.duzenleneblir ? 'pointer' : 'default',
                              boxSizing: 'border-box',
                            }}
                          >
                            <div style={{ fontSize: 12, fontWeight: 700, color: r.text, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {ev.baslik}
                            </div>
                            <div style={{ fontSize: 10, color: r.text, opacity: 0.75, marginTop: 1 }}>
                              {s5(ev.baslangic_saat)}–{s5(ev.bitis_saat)}
                            </div>
                            {ev.alt && h > 48 && (
                              <div style={{ fontSize: 10, color: r.text, opacity: 0.6, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {ev.alt}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal ───────────────────────────────────────────────────────── */}
      {modal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: card, borderRadius: 14, padding: 28, width: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 17 }}>
              {modal.id ? 'Özel Dersi Düzenle' : 'Yeni Özel Ders / Çalışma'}
            </h3>

            <label style={lblDynamic}>Başlık *</label>
            <input style={inpDynamic} value={modal.form.baslik} onChange={e => setF('baslik', e.target.value)} placeholder="ör. Prova — Ahmet & Zeynep" />

            <label style={lblDynamic}>Tür</label>
            <select style={inpDynamic} value={modal.form.tip} onChange={e => setF('tip', e.target.value)}>
              <option value="ozel_ders">Özel Ders</option>
              <option value="prova">Prova</option>
              <option value="calisma">Çalışma</option>
              <option value="diger">Diğer</option>
            </select>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div>
                <label style={lblDynamic}>Tarih *</label>
                <input style={inpDynamic} type="date" value={modal.form.tarih} onChange={e => setF('tarih', e.target.value)} />
              </div>
              <div>
                <label style={lblDynamic}>Başlangıç *</label>
                <input style={inpDynamic} type="time" value={modal.form.baslangic_saat} onChange={e => setF('baslangic_saat', e.target.value)} />
              </div>
              <div>
                <label style={lblDynamic}>Bitiş *</label>
                <input style={inpDynamic} type="time" value={modal.form.bitis_saat} onChange={e => setF('bitis_saat', e.target.value)} />
              </div>
            </div>

            <label style={lblDynamic}>Salon</label>
            <select style={inpDynamic} value={modal.form.salon_id} onChange={e => setF('salon_id', e.target.value)}>
              <option value="">— Salon seçin (opsiyonel)</option>
              {veri?.salonlar.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
            </select>

            {isAdmin && (
              <>
                <label style={lblDynamic}>Eğitmen</label>
                <select style={inpDynamic} value={modal.form.egitmen_id} onChange={e => setF('egitmen_id', e.target.value)}>
                  <option value="">— Eğitmen seçin (opsiyonel)</option>
                  {egitmenler.map(e => <option key={e.id} value={e.id}>{e.ad} {e.soyad}</option>)}
                </select>
              </>
            )}

            <label style={lblDynamic}>Öğrenci / Katılımcı adı (opsiyonel)</label>
            <input style={inpDynamic} value={modal.form.ogrenci_ad} onChange={e => setF('ogrenci_ad', e.target.value)} placeholder="ör. Zeynep K." />

            <label style={lblDynamic}>Not (opsiyonel)</label>
            <textarea style={{ ...inpDynamic, height: 68, resize: 'vertical' }} value={modal.form.notlar} onChange={e => setF('notlar', e.target.value)} placeholder="Özel notlar…" />

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              {modal.id && (
                <button onClick={sil} style={{ ...btnSt('#dc2626'), marginRight: 'auto' }}>Sil</button>
              )}
              <button onClick={kapat} style={btnSt('#aaa')}>İptal</button>
              <button onClick={kaydet} disabled={kaydediyor} style={btnSt('#3C3489')}>
                {kaydediyor ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stil sabitler ────────────────────────────────────────────────────────────
const navBtn: React.CSSProperties = {
  border: '1px solid #ddd', background: 'white', borderRadius: 6,
  padding: '5px 12px', cursor: 'pointer', fontSize: 16, color: '#555',
};
const ekleBtn: React.CSSProperties = {
  background: '#3C3489', color: 'white', border: 'none', borderRadius: 8,
  padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
};
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, marginTop: 12,
};
const inp: React.CSSProperties = {
  width: '100%', padding: '8px 10px', border: '1px solid #ddd',
  borderRadius: 7, fontSize: 13, boxSizing: 'border-box', outline: 'none',
};
const btnSt = (bg: string): React.CSSProperties => ({
  background: bg, color: 'white', border: 'none', borderRadius: 8,
  padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
});
